class BrandShowcase extends HTMLElement {
  constructor() {
    super();
    this._searchInput = null;
    this._alphaBar = null;
    this._grid = null;
    this._statusRegion = null;
    this._cards = [];
    this._activeFilter = 'all';
    this._searchQuery = '';
    this._debouncedSearch = null;
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
  }

  connectedCallback() {
    this._sectionId = this.dataset.sectionId;
    this._dimEmpty = this.dataset.dimEmpty === 'true';
    this._alphaFilterEnabled = this.dataset.alphaFilter === 'true';

    this._cacheElements();
    this._preComputeCardData();

    if (this._alphaFilterEnabled && this._alphaBar) {
      this._dimEmptyLetters();
      this._bindAlphaBar();
      this._bindAlphaOverflow();
    }

    if (this._searchInput) {
      this._debouncedSearch = debounce(this._onSearch.bind(this), 200);
      this._searchInput.addEventListener('input', this._debouncedSearch);
    }

    document.addEventListener('shopify:section:load', this._handleSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this._handleSectionLoad);
    if (this._updateAlphaOverflow) {
      window.removeEventListener('resize', this._updateAlphaOverflow);
    }
  }

  _handleSectionLoad(e) {
    if (e.detail.sectionId === this._sectionId) {
      this.disconnectedCallback();
      this.connectedCallback();
    }
  }

  _cacheElements() {
    this._grid = this.querySelector('[data-brand-grid]');
    this._statusRegion = this.querySelector('[data-brand-status]');
    this._alphaBar = this.querySelector('[data-alpha-bar]');
    this._alphaWrap = this.querySelector('[data-alpha-wrap]');
    this._searchInput = this.querySelector('.brand-showcase__search-input');
    this._cards = this._grid ? Array.from(this._grid.querySelectorAll('[data-brand-item]')) : [];
  }

  _bindAlphaOverflow() {
    if (!this._alphaBar || !this._alphaWrap) return;

    this._updateAlphaOverflow = () => {
      const el = this._alphaBar;
      const atStart = el.scrollLeft <= 1;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      this._alphaWrap.toggleAttribute('data-overflow-start', !atStart);
      this._alphaWrap.toggleAttribute('data-overflow-end', !atEnd);
    };

    this._alphaBar.addEventListener('scroll', this._updateAlphaOverflow, { passive: true });
    window.addEventListener('resize', this._updateAlphaOverflow, { passive: true });
    requestAnimationFrame(this._updateAlphaOverflow);
  }

  _normalize(str) {
    if (!str) return '';
    return str
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();
  }

  _preComputeCardData() {
    this._cards.forEach((card) => {
      const labelEl = card.querySelector('.brand-showcase__label');
      const rawLabel = labelEl ? labelEl.textContent.trim() : '';
      card.__label = this._normalize(rawLabel);
      const firstChar = this._normalize(rawLabel).charAt(0).toUpperCase();
      card.__letter = /[A-Z]/.test(firstChar) ? firstChar : '#';
    });
  }

  _dimEmptyLetters() {
    if (!this._alphaBar || !this._dimEmpty) return;

    const presentLetters = new Set(this._cards.map((c) => c.__letter));
    const pills = this._alphaBar.querySelectorAll('[data-letter]');

    pills.forEach((pill) => {
      const letter = pill.dataset.letter;
      if (letter === 'all') return;

      if (!presentLetters.has(letter)) {
        pill.classList.add('is-disabled');
        pill.setAttribute('aria-disabled', 'true');
        pill.setAttribute('tabindex', '-1');
      } else {
        pill.classList.remove('is-disabled');
        pill.removeAttribute('aria-disabled');
        pill.removeAttribute('tabindex');
      }
    });
  }

  _bindAlphaBar() {
    if (!this._alphaBar) return;

    this._alphaBar.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-letter]');
      if (!pill || pill.getAttribute('aria-disabled') === 'true') return;

      this._activeFilter = pill.dataset.letter;
      this._updateActivePill(pill);
      this._applyFilters();
    });

    this._alphaBar.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;

      const pills = Array.from(
        this._alphaBar.querySelectorAll('[data-letter]:not([aria-disabled="true"])')
      );
      const current = document.activeElement;
      const idx = pills.indexOf(current);
      if (idx === -1) return;

      e.preventDefault();
      const next = e.key === 'ArrowRight' ? pills[idx + 1] : pills[idx - 1];
      if (next) next.focus();
    });
  }

  _updateActivePill(activePill) {
    if (!this._alphaBar) return;
    this._alphaBar.querySelectorAll('[data-letter]').forEach((pill) => {
      const isActive = pill === activePill;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-pressed', String(isActive));
    });
  }

  _onSearch() {
    this._searchQuery = this._normalize(this._searchInput.value);
    this._applyFilters();
  }

  _applyFilters() {
    let visibleCount = 0;

    this._cards.forEach((card) => {
      const matchesSearch =
        this._searchQuery === '' || card.__label.includes(this._searchQuery);

      const matchesAlpha =
        this._activeFilter === 'all' || card.__letter === this._activeFilter;

      const visible = matchesSearch && matchesAlpha;
      card.hidden = !visible;
      if (visible) visibleCount++;
    });

    this._announceResults(visibleCount);
  }

  _announceResults(count) {
    if (!this._statusRegion) return;
    if (count === 1) {
      this._statusRegion.textContent = this._statusRegion.dataset.resultsOne || '1';
    } else {
      const template = this._statusRegion.dataset.resultsOther || '__COUNT__';
      this._statusRegion.textContent = template.replace('__COUNT__', count);
    }
  }
}

if (!customElements.get('brand-showcase')) {
  customElements.define('brand-showcase', BrandShowcase);
}
